import React, {
  useState,
  createContext,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { parseCookies } from "nookies";
import Swal from "sweetalert2";
import AlertResponses from "../jsx/components/Alert";
import { WEBSOCKET_URL } from "../api/Urls";
import useNotificationSounds from "../hooks/NotificationsHook";
import NotificationService from "../services/NotificationsServices";
import { ChangeBotStatus } from "../services/WaBottyServices";

const RECONNECT_INTERVAL = 5000;
const PING_INTERVAL = 10000;

const NotificationsContext = createContext();

export function NotificationsProvider({ children }) {
  const { token: _token } = parseCookies();
  const { ATENDFLOW_AID: account_id } = parseCookies();
  const {
    playNotificationSound,
    playHumanRequestSound,
    pauseHumanRequestSound,
  } = useNotificationSounds();

  // Refs
  const token = useRef(_token);
  const ws = useRef(null);
  const pingIntervalId = useRef(null);
  const reconnectTimeoutId = useRef(null);
  const helpData = useRef(null);
  const humanRequestNotification = useRef(false);

  // State
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [notify, setNotify] = useState({
    title: "",
    message: "",
    type: "",
    visible: false,
  });
  const [wsReadyState, setWsReadyState] = useState(0);
  const [unreadCounts, setUnreadCounts] = useState({});

  const Toast = Swal.mixin({
    toast: true,
    position: "bottom-end",
    timer: 30000,
    timerProgressBar: true,
    showConfirmButton: true,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    },
    customClass: {
      popup: "swal2-toast-custom",
      title: "swal2-toast-title",
      htmlContainer: "swal2-toast-content",
    },
  });

  // WebSocket message handler otimizado
  const handleWebSocketMessage = useCallback(
    (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data && typeof data === "object") {
          // Verifica se a notificação tem account_id e se corresponde ao usuario atual
          const isForCurrentStore =
            !data.account_id ||
            data.account_id.toString() === account_id.toString();
          if (!isForCurrentStore) {
            return; // Ignora notificações de outras lojas
          }
          if (data?.new_schedule) {
            

            Toast.fire({
              icon: "success",
              title: "Novo Agendamento!",
              html: "Você tem um novo agendamento! Atualize sua agenda.",
            });
          } else if (data.wa_notify) {
            // window.postMessage({ playWaSound: true }, "*");
            playNotificationSound();
            window.postMessage({ newWaMessage: data.wa_notify.message });
          } else if (data.wa_sent_notify) {
            if (
              data.wa_sent_notify.message.includes("humano") ||
              data.wa_sent_notify.message.includes("humana")
            ) {
              humanSupportRequest(data.wa_sent_notify.message);
            }
            window.postMessage({
              newWaSentMessage: data.wa_sent_notify.message,
            });
          }else if (data?.new_schedule_confirmation) {
            Toast.fire({
              icon: "success",
              title: "Nova Confirmação!",
              html: data?.new_schedule_confirmation.message,
            });
          } else if (data?.new_schedule_cancellation) {
            Toast.fire({
              icon: "info",
              title: "Cliente cancelou!",
              html: data?.new_schedule_cancellation.message,
            });
          } else if (data.wa_notify_success_send) {
            window.postMessage({
              waMessageSuccess: data.wa_notify_success_send.message,
            });
          } else if (data.wa_notify_error_send) {
            window.postMessage({
              waMessageError: data.wa_notify_error_send.message,
            });
          } else if (data.pix_id) {
            window.postMessage({ pix_id: data.pix_id });
          } else if (data.customer_requested_help) {
            helpData.current = data.customer_requested_help;
            setShowHelpModal(true);
          } else if (data.order_refund_error) {
            const { order_number, customer_name, message } =
              data.order_refund_error.message;
            Swal.fire({
              icon: "warning",
              title: "Oops!",
              html: `O pedido (#${order_number}) do <strong>${customer_name}</strong> não pode ser estornado. Motivo: ${message}`,
              showCancelButton: true,
              confirmButtonColor: "var(--primary)",
              cancelButtonColor: "var(--subtitles)",
              confirmButtonText: "Acessar painel",
              cancelButtonText: "Cancelar",
            }).then((value) => {
              if (value.isConfirmed) {
                window.open("https://www.asaas.com/dashboard/index");
              }
            });
          }
        }
      } catch (error) {
        console.error("Error processing WebSocket message:", error);
      }
    },
    [account_id]
  );

  // WebSocket setup
  useEffect(() => {
    const setupWebSocket = () => {
      if (!account_id || !token.current) return; // Não conecta sem account_id
      if (ws.current?.readyState === WebSocket.OPEN) return;

      const socketUrl = `${WEBSOCKET_URL}notifications/?ct=1&token=${token.current}&account_id=${account_id}`;
      ws.current = new WebSocket(socketUrl);

      ws.current.onopen = () => {
        setWsReadyState(WebSocket.OPEN);

        if (pingIntervalId.current) {
          clearInterval(pingIntervalId.current);
        }

        pingIntervalId.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: "ping" }));
          }
        }, PING_INTERVAL);
      };

      ws.current.onmessage = handleWebSocketMessage;

      ws.current.onclose = () => {
        setWsReadyState(WebSocket.CLOSED);

        if (pingIntervalId.current) {
          clearInterval(pingIntervalId.current);
        }

        reconnectTimeoutId.current = setTimeout(
          setupWebSocket,
          RECONNECT_INTERVAL
        );
      };

      ws.current.onerror = () => {
        ws.current?.close();
      };
    };

    setupWebSocket();

    return () => {
      if (ws.current) ws.current.close();
      if (pingIntervalId.current) clearInterval(pingIntervalId.current);
      if (reconnectTimeoutId.current) clearTimeout(reconnectTimeoutId.current);
    };
  }, [handleWebSocketMessage]);

  useEffect(() => {
    const tabWhatsapp = document.querySelector('[data-rb-event-key="5"]');
    const unread = Object.values(unreadCounts).some((valor) => valor > 0);

    if (unread) {
      if (tabWhatsapp) {
        let i = document.createElement("i");
        i.className = "fa fa-circle pulsate-bck";
        tabWhatsapp.appendChild(i);
      }
    } else {
      if (tabWhatsapp) {
        tabWhatsapp.querySelector(".fa-circle")?.remove();
      }
    }
  }, [unreadCounts]);

  const sendMessage = useCallback((message) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(
        typeof message === "string" ? message : JSON.stringify(message)
      );
      return true;
    }
    console.warn("WebSocket is not connected. Message not sent:", message);
    return false;
  }, []);

  function extractPhone(string) {
    const regex = /Telefone:\s*(\+?\d+)/i;

    const match = string.match(regex);

    if (match) {
      return match[1];
    }

    return false;
  }

  const humanSupportRequest = async (message) => {
    let settings = localStorage.getItem("_ACCSETTINGS_");
    if (settings) {
      settings = JSON.parse(settings);
      let notificationsTypes;
      try {
        notificationsTypes = JSON.parse(
          settings.notifications.notification_types_json
        );
      } catch (error) {
        notificationsTypes = settings.notifications.notification_types_json;
      }

      if (notificationsTypes.indexOf("platform") > -1) {
        setTimeout(() => {
          playHumanRequestSound();
        }, 1000);
        if (!humanRequestNotification.current) {
          humanRequestNotification.current = true;
          Swal.fire({
            title: "Atenção!",
            html: `Cliente solicitou atendimento humanizado!`,
            icon: "info",
            showConfirmButton: true,
            confirmButtonText: "Entendi",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then((result) => {
            if (result.isConfirmed) {
              console.log("pauseHumanRequestSound: true");
              pauseHumanRequestSound();
              setTimeout(() => {
                humanRequestNotification.current = false;
              }, 60 * 60 * 2);
            }
          });
        }
      }

      if (notificationsTypes.indexOf("whatsapp") > -1) {
        try {
          await NotificationService.sendWhatsapp();
        } catch (error) {}
      }
    }

    let bottyDataJson = sessionStorage.getItem("botty_account");

    if (message && bottyDataJson) {
      try {
        let bottyData = JSON.parse(bottyDataJson);
        let phone = extractPhone(message);
        if (phone) {
          console.log("extractPhone: true");
          let payload = {
            uid: bottyData.uid,
            phone: phone,
            bot_status: 0,
            human_request: 1,
          };
          ChangeBotStatus(payload).then((res) => console.log(res));
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  return (
    <NotificationsContext.Provider
      value={{
        notify,
        setNotify,
        helpData,
        showHelpModal,
        setShowHelpModal,
        wsReadyState,
        sendMessage,
        unreadCounts,
        setUnreadCounts,
      }}
    >
      {children}
      {notify.visible && (
        <AlertResponses
          title={notify.title}
          message={notify.message}
          type={notify.type}
          visible={notify.visible}
          setNotify={setNotify}
        />
      )}
    </NotificationsContext.Provider>
  );
}

export default NotificationsContext;
